import {
  Home,
  Images,
  MessageCircle,
  Brain,
  BookOpen,
  ScrollText,
  LineChart,
  Camera,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/**
 * The authenticated navigation, in one place because two shells render it: the
 * `lg` sidebar and the mobile Sheet. Duplicating the list is how the two drift.
 *
 * Phase 15 takes this from four items to eight. Four of the added surfaces were
 * already built, routed and tested, and linked from nowhere — `/app/becoming`
 * since Phase 8, `/app/insights` since Phase 13, `/app/photos` since Phase 14.
 * They were reachable only by typing the URL. This list is the fix.
 *
 * `primary` marks the two mirrors. Per the operator's thesis — "the image is
 * the visual mirror, the agent helps and mirrors the user" — the conversation
 * is not recovered debt filed under tools; it sits adjacent to Home.
 *
 * Deliberately absent: `/app/visions`, the gallery. `VisionGallery` is a
 * complete component mounted nowhere, and it cannot mount until two product
 * questions are answered (APP-EXPERIENCE.md §9): what a chapter is *called*
 * (the component renders `headline`, which exists nowhere server-side), and
 * whether milestone images contain the user's face (the reference set resolves
 * only for `evolution_stage === 'anchor'`, so every later chapter is generated
 * from text alone). Linking a headless card that shows a stranger would look
 * finished while being neither.
 */
export type NavItem = {
  to: string;
  i18nKey: string;
  icon: LucideIcon;
  primary?: boolean;
  /** Exact match only — without this, `/app` matches every child route. */
  end?: boolean;
};

export const navItems: readonly NavItem[] = [
  { to: '/app', i18nKey: 'app.sidebar.home', icon: Home, primary: true, end: true },
  { to: '/app/becoming', i18nKey: 'app.sidebar.becoming', icon: MessageCircle, primary: true },
  { to: '/app/visions', i18nKey: 'app.sidebar.visions', icon: Images, primary: true },
  // Promoted out of Settings (APP-EXPERIENCE.md §5). PROFILE / VISION /
  // PATTERNS is the transparency artifact CLAUDE.md calls brand-critical and
  // not negotiable; it sat behind a Settings tab whose selection is restored
  // from localStorage, so a user who last viewed Billing never even landed on
  // it. Filing the transparency artifact next to invoices is a positioning
  // error, not a layout one.
  { to: '/app/memory', i18nKey: 'app.sidebar.memory', icon: Brain },
  { to: '/app/evidence', i18nKey: 'app.sidebar.evidence', icon: BookOpen },
  { to: '/app/commitments', i18nKey: 'app.sidebar.commitments', icon: ScrollText },
  { to: '/app/insights', i18nKey: 'app.sidebar.insights', icon: LineChart },
  { to: '/app/photos', i18nKey: 'app.sidebar.photos', icon: Camera },
] as const;

/**
 * Settings, and everything else that is about the ACCOUNT rather than about
 * becoming, lives in the user menu under the identity line (operator,
 * 2026-08-16: "maybe we bring this as part of a user menu on the bottom-left
 * email. The sidebar should be reserved for the user flow").
 *
 * The distinction is worth stating because it decides where future entries go:
 * `navItems` is the flow, the sequence of surfaces a person moves through while
 * becoming someone. Settings is not a step in that; it is the drawer you open
 * when something about the machinery needs changing. Filing it in the flow made
 * the flow read as nine equal things, one of which was plumbing.
 *
 * Kept as a list rather than inlined for the same reason `navItems` is: two
 * shells render it (the `lg` sidebar dropdown and the mobile Sheet), and
 * duplicating it is how the two drift.
 */
export const accountItems: readonly NavItem[] = [
  { to: '/app/settings', i18nKey: 'app.sidebar.settings', icon: Settings },
] as const;
